import { ICoinsChartData } from "../types/coins";
import { ChallengeDriverResponse } from "../types/gamification";
import api from "./index";

export const getTotalScoreByAccount = async (): Promise<number> => {
  const response = await api.get<{
    data: {
      message: string;
      result: number;
    };
  }>('/v1/routes/gamification/score/total/account');

  return response.data.data.result ?? 0;
};

export const getScoreChartData = async (startDate?: string, endDate?: string): Promise<ICoinsChartData[]> => {
  const params: Record<string, any> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await api.get<{
    data: {
      message: string;
      result: Array<{
        month: string;
        totalPoints: number;
      }>;
    };
  }>('/v1/routes/gamification/challenge-driver/score-month', { params });

  const rawData = response.data.data.result ?? [];

  return rawData.map(item => {
    const date = new Date(item.month);
    return {
      mes: date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
      mesExtenso: date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      valor: item.totalPoints,
      date: new Date(date.getFullYear(), date.getMonth(), 1),
    };
  }).sort((a, b) => a.date.getTime() - b.date.getTime());
};

export const getScoreChartDataByDay = async (startDate?: string, endDate?: string): Promise<ICoinsChartData[]> => {
  const params: Record<string, any> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  console.log('Tentando buscar dados diários via API:', params);

  try {
    // Tenta usar endpoint específico para dados diários
    const response = await api.get<{
      data: {
        message: string;
        result: Array<{
          date: string;
          totalPoints: number;
        }>;
      };
    }>('/v1/routes/gamification/challenge-driver/score-day', { params });

    const rawData = response.data.data.result ?? [];
    console.log('Dados recebidos da API:', rawData);

    const chartData = rawData.map(item => {
      const date = new Date(item.date);
      return {
        mes: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        mesExtenso: date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }),
        valor: item.totalPoints,
        date: date,
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime());

    console.log('Dados processados da API:', chartData);
    return chartData;
  } catch (error) {
    console.warn('Endpoint score-day não disponível, usando dados de desafios para gerar gráfico diário');
    return await generateDailyScoreFromChallenges(startDate, endDate);
  }
};
const names = {
  "driverName": "driverName",
  "challegeName": "challegeName",
  "routeName": "routeName",
  "destination": "destination",
  "tripDate": "tripDate",
  "vwcoin": "vwcoin",
} as const;

export const getChallengeDriverPerAccount = async (
  page: number = 0,
  size: number = 10,
  driverName?: string,
  startDate?: string,
  endDate?: string,
  fieldSort?: string,
  sort: string = "desc",
): Promise<ChallengeDriverResponse> => {

  const params: Record<string, any> = { page, size };
  if (driverName) params.driverName = driverName;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (sort) params.order = sort;
  if (fieldSort && fieldSort in names) params.orderBy = names[fieldSort as keyof typeof names];

  const response = await api.get<{
    data: {
      message: string;
      result: ChallengeDriverResponse;
    };
  }>('/v1/routes/gamification/challenge-driver/challenge-driver', { params });

  return response.data.data.result;
};

export const generateDailyScoreFromChallenges = async (startDate?: string, endDate?: string): Promise<ICoinsChartData[]> => {
  try {
    console.log('Gerando dados diários para período:', { startDate, endDate });

    // Busca todos os desafios do período
    const response = await getChallengeDriverPerAccount(0, 1000, undefined, startDate, endDate);
    console.log('Desafios encontrados:', response.content.length);

    // Agrupa por data e soma os pontos
    const dailyScores = new Map<string, number>();

    response.content.forEach(challenge => {
      if (challenge.tripDate) {
        const date = new Date(challenge.tripDate);
        const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        const currentPoints = dailyScores.get(dateKey) || 0;
        const newPoints = currentPoints + (challenge.points || 0);
        dailyScores.set(dateKey, newPoints);
        console.log(`Data: ${dateKey}, Pontos: ${challenge.points}, Total do dia: ${newPoints}`);
      }
    });

    console.log('Dados agrupados por dia:', Array.from(dailyScores.entries()));

    // Converte para o formato esperado pelo gráfico
    const chartData: ICoinsChartData[] = Array.from(dailyScores.entries()).map(([dateKey, points]) => {
      const date = new Date(dateKey);
      return {
        mes: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        mesExtenso: date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }),
        valor: points,
        date: date,
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime());

    console.log('Dados finais do gráfico:', chartData);
    return chartData;
  } catch (error) {
    console.error('Erro ao gerar dados diários dos desafios:', error);
    return [];
  }
};

export const generateMonthlyScoreFromChallenges = async (startDate?: string, endDate?: string): Promise<ICoinsChartData[]> => {
  try {
    // Busca todos os desafios do período
    const response = await getChallengeDriverPerAccount(0, 1000, undefined, startDate, endDate);

    // Agrupa por mês e soma os pontos
    const monthlyScores = new Map<string, number>();

    response.content.forEach(challenge => {
      if (challenge.tripDate) {
        const date = new Date(challenge.tripDate);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM
        const currentPoints = monthlyScores.get(monthKey) || 0;
        monthlyScores.set(monthKey, currentPoints + (challenge.points || 0));
      }
    });

    // Converte para o formato esperado pelo gráfico
    const chartData: ICoinsChartData[] = Array.from(monthlyScores.entries()).map(([monthKey, points]) => {
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return {
        mes: date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        mesExtenso: date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
        valor: points,
        date: date,
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime());

    return chartData;
  } catch (error) {
    console.error('Erro ao gerar dados mensais dos desafios:', error);
    return [];
  }
};

